/**
 * 平台统一会话状态
 *
 * 用户与积分的全局单一事实来源：
 *  - 初始值来自本地缓存（authSession），挂载后按需 getMe 刷新；
 *  - 监听 platform:http 的 401 广播（platform:unauthorized），会话失效时同步清空 React 状态；
 *  - 监听支付完成事件（credits-updated），跨页面同步余额；
 *  - 页面恢复可见时自动刷新（与历史 App.tsx 行为一致）。
 *
 * 页面迁移策略：逐页从本地 user/credits useState 切换到 usePlatformSession()；
 * 未迁移页面的旧状态在过渡期继续工作。
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { api } from '../services/api';
import { saveActiveUser } from '../utils/authSession';
import { UNAUTHORIZED_EVENT } from './http';
import type { UserInfo } from '../baselines/image';

interface PlatformSessionContextValue {
  user: UserInfo | null;
  credits: number;
  isLoggedIn: boolean;
  isAdmin: boolean;
  isImpersonating: boolean;
  /** 以服务端为准刷新用户与积分（getMe 内部会 saveActiveUser 回写缓存） */
  refreshUser: () => Promise<UserInfo | null>;
  /** 登录/更新成功后设置用户（同步回写本地缓存） */
  setUser: (user: UserInfo | null) => void;
  /** 本地积分增减（提交任务后的乐观扣减，之后仍以服务端为准） */
  applyCreditsDelta: (delta: number) => void;
  /** 退出登录：清本地会话并清空状态 */
  logout: () => void;
}

const PlatformSessionContext = createContext<PlatformSessionContextValue | null>(null);

export function PlatformSessionProvider({ children }: { children: ReactNode }) {
  const [user, setUserState] = useState<UserInfo | null>(() => api.getSavedUser());
  const [credits, setCredits] = useState<number>(() => user?.credits ?? 0);
  // refreshUser 的引用在事件监听中保持最新 user，避免闭包读到旧值
  const userRef = useRef(user);
  userRef.current = user;

  const setUser = useCallback((nextUser: UserInfo | null) => {
    setUserState(nextUser);
    setCredits(nextUser?.credits ?? 0);
    if (nextUser) saveActiveUser(nextUser);
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const nextUser = await api.getMe();
      setUserState(nextUser);
      setCredits(nextUser.credits);
      return nextUser;
    } catch {
      return null;
    }
  }, []);

  const applyCreditsDelta = useCallback((delta: number) => {
    setCredits((current) => Math.max(0, current + delta));
    setUserState((current) => (current ? { ...current, credits: Math.max(0, current.credits + delta) } : current));
  }, []);

  const logout = useCallback(() => {
    api.logout();
    setUserState(null);
    setCredits(0);
  }, []);

  // 401 广播：http 层已清理 storage，这里同步清空 React 状态
  useEffect(() => {
    const handleUnauthorized = () => {
      setUserState(null);
      setCredits(0);
    };
    window.addEventListener(UNAUTHORIZED_EVENT, handleUnauthorized);
    return () => window.removeEventListener(UNAUTHORIZED_EVENT, handleUnauthorized);
  }, []);

  // 支付完成后同步余额（PaymentResultPage 派发）
  useEffect(() => {
    const handleCreditsUpdated = (event: Event) => {
      const detail = (event as CustomEvent<{ credits?: number }>).detail;
      if (typeof detail?.credits === 'number') {
        setCredits(detail.credits);
        setUserState((current) => (current ? { ...current, credits: detail.credits! } : current));
      }
    };
    window.addEventListener('credits-updated', handleCreditsUpdated);
    return () => window.removeEventListener('credits-updated', handleCreditsUpdated);
  }, []);

  // 页面恢复可见时刷新（已登录才请求），与历史 App.tsx 行为一致
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && userRef.current) {
        void refreshUser();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [refreshUser]);

  const value = useMemo<PlatformSessionContextValue>(
    () => ({
      user,
      credits,
      isLoggedIn: !!user,
      isAdmin: user?.role === 'admin',
      isImpersonating: !!user?.impersonation?.active,
      refreshUser,
      setUser,
      applyCreditsDelta,
      logout,
    }),
    [user, credits, refreshUser, setUser, applyCreditsDelta, logout]
  );

  return <PlatformSessionContext.Provider value={value}>{children}</PlatformSessionContext.Provider>;
}

export function usePlatformSession(): PlatformSessionContextValue {
  const context = useContext(PlatformSessionContext);
  if (!context) {
    throw new Error('usePlatformSession 必须在 PlatformSessionProvider 内使用');
  }
  return context;
}
